import React, { useState } from "react";
import { useNavigate } from "react-router-dom";
import { useAuth } from "@/context/AuthContext";
import { useSettings } from "@/context/SettingsContext";
import { useVocab } from "@/lib/vocab";
import api, { apiError, API } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { PasswordInput } from "@/components/PasswordInput";
import { toast } from "sonner";

const HERO = "https://images.pexels.com/photos/27692895/pexels-photo-27692895.jpeg";

export default function Login() {
  const { login } = useAuth();
  const { settings } = useSettings();
  const voc = useVocab();
  const nav = useNavigate();
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [err, setErr] = useState("");
  const [forgot, setForgot] = useState(false);
  const [loading, setLoading] = useState(false);
  const brand = settings?.company_name || "Fiableo";

  const submit = async (e) => {
    e.preventDefault();
    setErr(""); setLoading(true);
    try { await login(email, password); nav("/"); }
    catch (e2) { setErr(apiError(e2.response?.data?.detail) || e2.message); }
    finally { setLoading(false); }
  };
  const doForgot = async (e) => {
    e.preventDefault();
    try { await api.post("/auth/forgot-password", { email }); toast.success("Si le compte existe, un email a été envoyé."); setForgot(false); }
    catch { toast.error("Erreur"); }
  };

  return (
    <div className="min-h-screen grid lg:grid-cols-2 bg-background">
      <div className="relative hidden lg:block grain">
        <img src={HERO} alt="" className="absolute inset-0 h-full w-full object-cover" />
        <div className="absolute inset-0 bg-primary/70" />
        <div className="relative h-full flex flex-col justify-between p-12 text-primary-foreground">
          <div className="font-display font-black text-2xl tracking-tight flex items-center gap-2">
            {settings?.logo_url && <img src={`${API}/branding/logo`} alt="" className="h-9 w-9 rounded-lg object-contain bg-white/10 p-0.5" onError={(e) => { e.currentTarget.style.display = "none"; }} />}
            {brand}
          </div>
          <div>
            <div className="text-xs uppercase tracking-overline text-accent mb-4">{voc.loginOverline}</div>
            <h1 className="font-display font-black text-4xl xl:text-5xl leading-[1.05]">La gestion de flotte, réinventée pour l'excellence.</h1>
            <p className="mt-6 text-primary-foreground/70 max-w-md">Calendrier partagé, dispatch sans chevauchement, facturation et pilotage temps réel.</p>
          </div>
        </div>
      </div>
      <div className="flex items-center justify-center p-6 md:p-12">
        <div className="w-full max-w-sm">
          <div className="lg:hidden font-display font-black text-2xl mb-8">{brand}</div>
          <h2 className="font-display font-bold text-3xl tracking-tight">{forgot ? "Mot de passe oublié" : "Connexion"}</h2>
          {forgot && settings?.email_enabled === false ? (
            // Sans e-mail configuré, aucun lien ne peut partir : c'est le gestionnaire qui crée le lien (écran Utilisateurs).
            <div className="mt-8 space-y-6" data-testid="forgot-no-email">
              <p className="text-sm">Contactez votre gestionnaire : il peut vous envoyer un lien de réinitialisation (par WhatsApp, par exemple).</p>
              <button type="button" className="text-sm text-muted-foreground hover:text-foreground w-full text-center" onClick={() => setForgot(false)} data-testid="toggle-forgot">
                ← Retour à la connexion
              </button>
            </div>
          ) : (<>
          <p className="text-muted-foreground mt-2 text-sm">{forgot ? "Entrez votre email pour réinitialiser." : "Accédez à votre espace de gestion."}</p>
          <form onSubmit={forgot ? doForgot : submit} className="mt-8 space-y-4">
            <div className="space-y-2">
              <Label htmlFor="email">Email</Label>
              <Input id="email" type="email" value={email} onChange={(e) => setEmail(e.target.value)} required data-testid="login-email" placeholder="vous@exemple.com" />
            </div>
            {!forgot && (
              <div className="space-y-2">
                <Label htmlFor="password">Mot de passe</Label>
                <PasswordInput id="password" value={password} onChange={(e) => setPassword(e.target.value)} required placeholder="••••••••" testid="login-password" />
              </div>
            )}
            {err && <p className="text-sm text-destructive" data-testid="login-error">{err}</p>}
            <Button type="submit" className="w-full" disabled={loading} data-testid="login-submit">
              {loading ? "…" : forgot ? "Envoyer le lien" : "Se connecter"}
            </Button>
            <button type="button" className="text-sm text-muted-foreground hover:text-foreground w-full text-center" onClick={() => setForgot(!forgot)} data-testid="toggle-forgot">
              {forgot ? "← Retour à la connexion" : "Mot de passe oublié ?"}
            </button>
          </form>
          </>)}
        </div>
      </div>
    </div>
  );
}
